import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ROLES } from '../constants/roles';
import AutocompleteField from './inputs/AutocompleteField';
import { getClientDisplayName } from '../utils/orderItemDisplay';

/**
 * Dialog to reassign an order to a different client (internal staff with manage_orders).
 */
export default function MoveOrderClientDialog({
  open,
  onClose,
  orderId,
  currentClientId,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [targetClientId, setTargetClientId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTargetClientId('');
    setError('');
    setClientsLoading(true);
    axios.get(`/api/users?role=${encodeURIComponent(ROLES.CLIENT)}`)
      .then((res) => {
        const list = (res.data || []).filter((u) => !u.disabled && String(u._id) !== String(currentClientId));
        setClients(list);
      })
      .catch(() => setError(t('orders.moveToClientLoadFailed')))
      .finally(() => setClientsLoading(false));
  }, [open, currentClientId, t]);

  const handleSubmit = async () => {
    if (!targetClientId) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.patch(`/api/orders/${orderId}/client`, { clientId: targetClientId });
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('orders.moveToClientFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('orders.moveToClient')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('orders.moveToClientHint')}
        </Typography>
        {clientsLoading ? (
          <CircularProgress size={24} />
        ) : (
          <AutocompleteField
            options={clients}
            value={targetClientId}
            onChange={(nextId) => setTargetClientId(nextId || '')}
            label={t('orders.moveToClientSelect')}
            getOptionLabel={(u) => getClientDisplayName(u)}
            getOptionValue={(u) => u._id}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            size="small"
            sx={{ width: '100%' }}
            disabled={submitting}
          />
        )}
        {error && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !targetClientId || clientsLoading}
          data-testid="move-order-client-confirm"
        >
          {submitting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {t('orders.moveToClientConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
